import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { networkLoggerStore } from '../../utils/networkLogger/networkLoggerStore';
import { NetworkLog } from '../../utils/networkLogger/types';
import { enableCustomNetworkLogger } from '../../constants/NetworkLoggerConstants';

const FAB_POS_KEY = 'networkLogger.fabPos.v1';
const FAB_SIZE = 48;

function isLoggerEnabled(): boolean {
    return enableCustomNetworkLogger && Capacitor.isNativePlatform();
}

function useIsNarrow(): boolean {
    const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 767px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = () => setNarrow(mq.matches);
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);
    return narrow;
}

function shellQuote(value: string): string {
    return `'${value.replace(/'/g, "'\\''")}'`;
}

function toCurl(log: NetworkLog): string {
    const parts: string[] = [`curl -X ${log.method}`, shellQuote(log.url)];
    const headers = log.requestHeaders || {};
    Object.keys(headers).forEach((key) => {
        parts.push(`-H ${shellQuote(`${key}: ${headers[key]}`)}`);
    });
    if (log.requestBody && log.method !== 'GET' && log.method !== 'HEAD') {
        parts.push(`--data-raw ${shellQuote(log.requestBody)}`);
    }
    return parts.join(' \\\n  ');
}

function copyText(text: string): void {
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
        return;
    }
    fallbackCopy(text);
}

function fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
        document.execCommand('copy');
    } catch {
        // clipboard unavailable — nothing else we can do
    }
    document.body.removeChild(textarea);
}

function formatHeaders(headers?: Record<string, string>): string {
    if (!headers || Object.keys(headers).length === 0) return '';
    return Object.keys(headers)
        .map((key) => `${key}: ${headers[key]}`)
        .join('\n');
}

function prettyBody(body?: string): string {
    if (!body) return '';
    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
        return body;
    }
}

function pathOf(url: string): string {
    try {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
    } catch {
        return url;
    }
}

function stateColor(log: NetworkLog): string {
    if (log.state === 'pending') return 'var(--orange, #E8A33D)';
    if (log.state === 'error') return 'var(--red, #E25C5C)';
    return 'var(--green, #3DBA6F)';
}

function clampFabPos(x: number, y: number): { x: number; y: number } {
    return {
        x: Math.min(Math.max(8, x), window.innerWidth - FAB_SIZE - 8),
        y: Math.min(Math.max(8, y), window.innerHeight - FAB_SIZE - 8),
    };
}

function loadFabPos(): { x: number; y: number } {
    try {
        const raw = localStorage.getItem(FAB_POS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
                return clampFabPos(parsed.x, parsed.y);
            }
        }
    } catch {
        // bad/missing stored position — use default
    }
    return clampFabPos(window.innerWidth - FAB_SIZE - 16, window.innerHeight - FAB_SIZE - 96);
}

const monoText: React.CSSProperties = {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 12,
    color: 'var(--t1, #1F2430)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
};

const smallButton: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: 'var(--radius-button, 6px)',
    border: '1px solid var(--card-border, #E3E6EE)',
    background: 'var(--surface, #FFFFFF)',
    color: 'var(--t2, #5A6172)',
    fontFamily: 'var(--font-primary, sans-serif)',
    fontSize: 12,
    cursor: 'pointer',
};

interface SectionProps {
    title: string;
    text: string;
}

function Section({ title, text }: SectionProps) {
    const [copied, setCopied] = useState(false);
    if (!text) return null;
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span
                    style={{
                        fontFamily: 'var(--font-primary, sans-serif)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--t2, #5A6172)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                    }}
                >
                    {title}
                </span>
                <button
                    type="button"
                    style={smallButton}
                    onClick={() => {
                        copyText(text);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1200);
                    }}
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre
                style={{
                    ...monoText,
                    background: 'var(--bg, #F5F6FA)',
                    border: '1px solid var(--divider, #ECEEF4)',
                    borderRadius: 'var(--radius-input, 8px)',
                    padding: 10,
                    maxHeight: 220,
                    overflow: 'auto',
                }}
            >
                {text}
            </pre>
        </div>
    );
}

interface LogRowProps {
    log: NetworkLog;
    selected: boolean;
    onSelect: (id: string) => void;
}

function LogRow({ log, selected, onSelect }: LogRowProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect(log.id)}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                border: 'none',
                borderBottom: '1px solid var(--divider, #ECEEF4)',
                background: selected ? 'var(--bg, #F5F6FA)' : 'transparent',
                cursor: 'pointer',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                    style={{
                        fontFamily: 'var(--font-mono, monospace)',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--t1, #1F2430)',
                        minWidth: 44,
                    }}
                >
                    {log.method}
                </span>
                <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, fontWeight: 700, color: stateColor(log) }}>
                    {log.state === 'pending' ? '…' : log.status ?? 'ERR'}
                </span>
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono, monospace)', fontSize: 10, color: 'var(--t3, #9CA3B8)' }}>
                    {log.durationMs != null ? `${log.durationMs}ms` : ''}
                </span>
            </div>
            <div
                style={{
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: 11,
                    color: 'var(--t2, #5A6172)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: 2,
                }}
            >
                {pathOf(log.url)}
            </div>
        </button>
    );
}

interface LogDetailProps {
    log: NetworkLog;
}

function LogDetail({ log }: LogDetailProps) {
    return (
        <div style={{ padding: 12, overflowY: 'auto', flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13, fontWeight: 700, color: 'var(--t1, #1F2430)' }}>
                        {log.method}
                    </span>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13, fontWeight: 700, color: stateColor(log) }}>
                        {log.state === 'pending' ? 'pending…' : `${log.status ?? ''} ${log.statusText ?? ''}`.trim() || log.state}
                    </span>
                    {log.durationMs != null && (
                        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--t3, #9CA3B8)' }}>
                            {log.durationMs}ms
                        </span>
                    )}
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--t3, #9CA3B8)' }}>{log.source}</span>
                </div>
                <div style={{ ...monoText, color: 'var(--t2, #5A6172)', marginTop: 6 }}>{log.url}</div>
                {log.error && <div style={{ ...monoText, color: 'var(--red, #E25C5C)', marginTop: 6 }}>{log.error}</div>}
            </div>
            <Section title="cURL" text={toCurl(log)} />
            <Section title="Request headers" text={formatHeaders(log.requestHeaders)} />
            <Section title="Request body" text={prettyBody(log.requestBody)} />
            <Section title="Response headers" text={formatHeaders(log.responseHeaders)} />
            <Section title="Response body" text={prettyBody(log.responseBody)} />
        </div>
    );
}

export default function NetworkLogger() {
    const enabled = isLoggerEnabled();
    const logs = useSyncExternalStore(networkLoggerStore.subscribe, networkLoggerStore.getSnapshot, networkLoggerStore.getSnapshot);
    const isNarrow = useIsNarrow();
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [fabPos, setFabPos] = useState(loadFabPos);
    const [copiedCurl, setCopiedCurl] = useState(false);
    const dragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number; moved: boolean } | null>(null);

    const filtered = useMemo(() => {
        const query = filter.trim().toLowerCase();
        if (!query) return logs;
        return logs.filter(
            (log) =>
                log.url.toLowerCase().includes(query) ||
                log.method.toLowerCase().includes(query) ||
                String(log.status ?? '').includes(query)
        );
    }, [logs, filter]);

    const selected = useMemo(() => filtered.find((log) => log.id === selectedId) ?? null, [filtered, selectedId]);

    if (!enabled) return null;

    const errorCount = logs.filter((log) => log.state === 'error').length;
    const pendingCount = logs.filter((log) => log.state === 'pending').length;
    const badgeCount = errorCount > 0 ? errorCount : pendingCount;
    const badgeColor = errorCount > 0 ? 'var(--red, #E25C5C)' : 'var(--orange, #E8A33D)';

    const onFabPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { pointerId: e.pointerId, offsetX: e.clientX - fabPos.x, offsetY: e.clientY - fabPos.y, moved: false };
    };

    const onFabPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        const next = clampFabPos(e.clientX - drag.offsetX, e.clientY - drag.offsetY);
        if (!drag.moved && Math.abs(next.x - fabPos.x) < 4 && Math.abs(next.y - fabPos.y) < 4) return;
        drag.moved = true;
        setFabPos(next);
    };

    const onFabPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        dragRef.current = null;
        if (drag.moved) {
            try {
                localStorage.setItem(FAB_POS_KEY, JSON.stringify(fabPos));
            } catch {
                // storage unavailable — position just won't persist
            }
        } else {
            setOpen(true);
        }
    };

    const showList = !isNarrow || !selected;
    const showDetail = !isNarrow || !!selected;

    return (
        <>
            {!open && (
                <button
                    type="button"
                    aria-label="Network logger"
                    onPointerDown={onFabPointerDown}
                    onPointerMove={onFabPointerMove}
                    onPointerUp={onFabPointerUp}
                    style={{
                        position: 'fixed',
                        left: fabPos.x,
                        top: fabPos.y,
                        width: FAB_SIZE,
                        height: FAB_SIZE,
                        borderRadius: '50%',
                        border: '1px solid var(--card-border, #E3E6EE)',
                        background: 'var(--surface, #FFFFFF)',
                        boxShadow: 'var(--shadow, 0 4px 14px rgba(0,0,0,0.12))',
                        zIndex: 2147483646,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        touchAction: 'none',
                    }}
                >
                    <span role="img" aria-hidden="true">
                        🛜
                    </span>
                    {badgeCount > 0 && (
                        <span
                            style={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                minWidth: 18,
                                height: 18,
                                padding: '0 4px',
                                borderRadius: 9,
                                background: badgeColor,
                                color: '#FFFFFF',
                                fontFamily: 'var(--font-primary, sans-serif)',
                                fontSize: 10,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                    )}
                </button>
            )}

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.45)',
                        zIndex: 2147483647,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 12,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 'min(960px, 100%)',
                            height: 'min(80vh, 720px)',
                            background: 'var(--surface, #FFFFFF)',
                            borderRadius: 'var(--radius-card, 14px)',
                            border: '1px solid var(--card-border, #E3E6EE)',
                            boxShadow: 'var(--card-shadow, 0 12px 32px rgba(0,0,0,0.2))',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '10px 12px',
                                borderBottom: '1px solid var(--divider, #ECEEF4)',
                            }}
                        >
                            {isNarrow && selected ? (
                                <button type="button" style={smallButton} onClick={() => setSelectedId(null)}>
                                    ←
                                </button>
                            ) : null}
                            <span
                                style={{
                                    fontFamily: 'var(--font-primary, sans-serif)',
                                    fontSize: 'var(--fs-h2, 16px)',
                                    fontWeight: 700,
                                    color: 'var(--t1, #1F2430)',
                                }}
                            >
                                Network
                            </span>
                            <span style={{ fontFamily: 'var(--font-primary, sans-serif)', fontSize: 12, color: 'var(--t3, #9CA3B8)' }}>
                                {filtered.length}/{logs.length}
                            </span>
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                {selected && (
                                    <button
                                        type="button"
                                        style={{
                                            ...smallButton,
                                            background: 'var(--accent, #D23A3B)',
                                            border: '1px solid var(--accent, #D23A3B)',
                                            color: '#FFFFFF',
                                            fontWeight: 600,
                                        }}
                                        onClick={() => {
                                            copyText(toCurl(selected));
                                            setCopiedCurl(true);
                                            setTimeout(() => setCopiedCurl(false), 1200);
                                        }}
                                    >
                                        {copiedCurl ? 'Copied!' : 'Copy cURL'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    style={smallButton}
                                    onClick={() => {
                                        networkLoggerStore.clear();
                                        setSelectedId(null);
                                    }}
                                >
                                    Clear
                                </button>
                                {!(isNarrow && selected) && (
                                    <button type="button" style={smallButton} onClick={() => setOpen(false)}>
                                        ✕
                                    </button>
                                )}
                            </span>
                        </div>

                        {showList && (
                            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--divider, #ECEEF4)' }}>
                                <input
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Filter by URL, method or status…"
                                    style={{
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        padding: '6px 10px',
                                        borderRadius: 'var(--radius-input, 8px)',
                                        border: '1px solid var(--card-border, #E3E6EE)',
                                        background: 'var(--bg, #F5F6FA)',
                                        color: 'var(--t1, #1F2430)',
                                        fontFamily: 'var(--font-primary, sans-serif)',
                                        fontSize: 13,
                                        outline: 'none',
                                    }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                            {showList && (
                                <div
                                    style={{
                                        width: isNarrow ? '100%' : '40%',
                                        overflowY: 'auto',
                                        borderRight: isNarrow ? 'none' : '1px solid var(--divider, #ECEEF4)',
                                    }}
                                >
                                    {filtered.length === 0 ? (
                                        <div
                                            style={{
                                                padding: 24,
                                                textAlign: 'center',
                                                fontFamily: 'var(--font-primary, sans-serif)',
                                                fontSize: 13,
                                                color: 'var(--t3, #9CA3B8)',
                                            }}
                                        >
                                            {logs.length === 0 ? 'No requests captured yet.' : 'No requests match the filter.'}
                                        </div>
                                    ) : (
                                        filtered.map((log) => (
                                            <LogRow key={log.id} log={log} selected={log.id === selectedId} onSelect={setSelectedId} />
                                        ))
                                    )}
                                </div>
                            )}
                            {showDetail && (
                                <div style={{ width: isNarrow ? '100%' : '60%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    {selected ? (
                                        <LogDetail log={selected} />
                                    ) : (
                                        <div
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontFamily: 'var(--font-primary, sans-serif)',
                                                fontSize: 13,
                                                color: 'var(--t3, #9CA3B8)',
                                            }}
                                        >
                                            Select a request to inspect it.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
